import { useLiveQuery } from "@hq/client-react";
import type { ContentConflict } from "@hq/contracts";
import { Button } from "@z/ui";

export function ConflictReview({
  conflict,
  disabled,
  onResolve,
}: {
  conflict: ContentConflict;
  disabled: boolean;
  onResolve: (resolution: "keep_server" | "keep_mine" | "keep_both") => void;
}) {
  const { data: current } = useLiveQuery(
    ["notes", "tasks"],
    async (store) =>
      conflict.kind === "task"
        ? store.tasks.get(conflict.entityId)
        : store.notes.get(conflict.entityId),
    [conflict.kind, conflict.entityId],
  );
  const incoming = conflict.mutation;
  const incomingText =
    "markdown" in incoming
      ? incoming.markdown
      : "title" in incoming
        ? incoming.title
        : "description" in incoming
          ? incoming.description
          : "note" in incoming
            ? incoming.note.markdown
            : "Preserved change";
  const currentText = !current
    ? "Sync this item to review its current version"
    : incoming.type === "notes.rename"
      ? current.title
      : "markdown" in current
        ? current.markdown
        : current.description;
  return (
    <details className="rounded-md border border-border p-3">
      <summary className="cursor-pointer text-sm">
        {current?.title ?? "Preserved content"} · {new Date(conflict.createdAt).toLocaleString()}
      </summary>
      <h4 className="mt-3 text-xs font-medium">
        Current version{current?.deletedAt ? " (in Trash)" : ""}
      </h4>
      <pre className="my-2 max-h-56 overflow-auto whitespace-pre-wrap text-xs">
        {currentText || "Empty"}
      </pre>
      <h4 className="mt-3 text-xs font-medium">Incoming version</h4>
      <pre className="my-2 max-h-56 overflow-auto whitespace-pre-wrap text-xs">
        {incomingText || "Empty"}
      </pre>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled}
          onClick={() => onResolve("keep_both")}
        >
          Keep both
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={disabled || conflict.reason === "tombstone"}
          onClick={() => onResolve("keep_mine")}
        >
          Keep incoming
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={() => onResolve("keep_server")}
        >
          Keep server version
        </Button>
      </div>
    </details>
  );
}
